import React, { useState, useEffect, useRef } from 'react';

/**
 * LazyVideo component to lazy load and play videos only when visible
 * Pauses video when out of viewport to optimize CPU/GPU battery.
 */
export default function LazyVideo({
  src,
  poster,
  className = "",
  posterClassName = "",
  placeholderBg = "bg-slate-900/10",
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!window.IntersectionObserver) {
      setInView(true);
      setHasIntersected(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) {
          setHasIntersected(true);
        }
      },
      { rootMargin: "200px", threshold: 0.01 }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      observer.disconnect();
    };
  }, []);



  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.load();
    }
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (inView) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // Autoplay was prevented
          console.warn("Autoplay prevented on lazy video:", error);
        });
      }
    } else {
      video.pause();
    }
  }, [inView, src]);

  const hasPosition = /absolute|relative|fixed|sticky/.test(className);
  const positionClass = hasPosition ? "" : "relative";

  return (
    <div
      ref={containerRef}
      className={`${positionClass} overflow-hidden ${placeholderBg} ${className}`}
      {...props}
    >
      {/* Poster preview while loading */}
      {!loaded && poster && (
        <img
          src={poster}
          alt="Video poster preview"
          className={`absolute inset-0 w-full h-full object-cover z-10 transition-opacity duration-700 ease-in-out opacity-100 ${posterClassName}`}
        />
      )}

      {/* Shimmer pulse until ready */}
      {!loaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-slate-100/10 via-slate-200/20 to-slate-100/10 animate-pulse z-10" />
      )}

      {hasIntersected && (
        <video
          ref={videoRef}
          src={src}
          autoPlay
          loop
          muted
          playsInline
          poster={poster}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          onCanPlay={() => setLoaded(true)}
        />
      )}
    </div>
  );
}
